import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  LIFECYCLE_MODULES,
  LIFECYCLE_ROUTE,
  buildSampleLifecycleRules,
  createEmptyLifecycleForm,
  formatLifecycleConditionsDisplay,
  formatLifecycleDateTime,
  getArchiveActionDefinition,
  getArchiveActionLabel,
  getArchiveActionsForScope,
  getAvailableLifecycleConditionFields,
  getEndEventDefinition,
  getEndEventLabel,
  getEndEventsForScope,
  getLifecycleModuleLabel,
  getRecordTypeLabel,
  nextLifecycleRuleId,
  normalizeLifecycleForm,
  recordTypesForModule,
} from '../../../data/lifecycleConfig';
import {
  canAddLifecycleRule,
  canEditLifecycleRule,
  canSaveLifecycleRule,
  canViewLifecycleConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveLifecycleRule,
} from '../../../data/lifecycleRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyLifecycleForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeLifecycleForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: `${getLabel(value) || value} (Unavailable)` }];
};

const LifecycleRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewLifecycleConfiguration(auth);
  const canAdd = canAddLifecycleRule(auth);
  const canEdit = canEditLifecycleRule(auth);
  const canSave = canSaveLifecycleRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleLifecycleRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const recordTypeOptions = useMemo(
    () => withCurrentOption(
      recordTypesForModule(formData.module),
      formData.recordType,
      (value) => getRecordTypeLabel(formData.module, value),
    ),
    [formData.module, formData.recordType],
  );
  const endEventOptions = useMemo(
    () => withCurrentOption(
      getEndEventsForScope(formData.module, formData.recordType),
      formData.endEvent,
      getEndEventLabel,
    ),
    [formData.module, formData.recordType, formData.endEvent],
  );
  const archiveActionOptions = useMemo(
    () => withCurrentOption(
      getArchiveActionsForScope(formData.module, formData.recordType, formData.endEvent),
      formData.archiveAction,
      getArchiveActionLabel,
    ),
    [formData.module, formData.recordType, formData.endEvent, formData.archiveAction],
  );

  const actionDetails = getArchiveActionDefinition(formData.archiveAction);
  const endEventDetails = getEndEventDefinition(formData.endEvent);
  const conditionFields = getAvailableLifecycleConditionFields();

  const clearFieldErrors = (field) => {
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
      activation: undefined,
      duplicate: undefined,
      overlap: undefined,
      conflict: undefined,
      concurrent: undefined,
      permission: undefined,
      checksUnavailable: undefined,
      form: undefined,
    }));
  };

  const handleChange = (field, value) => {
    clearFieldErrors(field);
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'module') {
        next.recordType = '';
        next.endEvent = '';
        next.lifecycleTrigger = '';
        next.archiveAction = '';
        next.archiveRule = '';
        next.conditions = [];
      }
      if (field === 'recordType') {
        next.endEvent = '';
        next.lifecycleTrigger = '';
        next.archiveAction = '';
        next.archiveRule = '';
        next.conditions = [];
      }
      if (field === 'endEvent') {
        next.lifecycleTrigger = value;
        next.archiveAction = '';
        next.archiveRule = '';
      }
      if (field === 'archiveAction') {
        next.archiveRule = value;
      }
      return next;
    });
  };

  const buildRecord = (nowIso, userLabel) => {
    const normalized = normalizeLifecycleForm(formData);
    const id = formData.id || nextLifecycleRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...normalized,
      id,
      ruleName: normalized.ruleName.trim(),
      changeReason: normalized.changeReason.trim(),
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: null,
      configurationHistory: formData.configurationHistory || [],
      testHistory: formData.testHistory || [],
      dateAdded: isNew ? nowIso : formData.dateAdded || formData.createdOn || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || formData.createdBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
      createdOn: isNew ? nowIso : formData.createdOn || formData.dateAdded || nowIso,
      createdBy: isNew ? userLabel : formData.createdBy || formData.addedBy || userLabel,
      modifiedOn: nowIso,
      modifiedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));

    if ((isCreate && !canAdd) || (isEdit && !canEdit) || !canSave) {
      setErrors({
        permission: 'You do not have permission to save this Rule.',
        activation: 'You do not have permission to save this Rule.',
      });
      return;
    }

    const record = buildRecord(nowIso, userLabel);
    let result;
    try {
      result = saveLifecycleRule(allRules, record, publishedBaseline || {}, {
        originalLastUpdated: resolvedSource.lastUpdated || resolvedSource.modifiedOn,
        canSave: true,
        checksAvailable: true,
      });
    } catch {
      setErrors({
        checksUnavailable: 'Required checks could not be completed. Try again.',
        activation: 'Required checks could not be completed. Try again.',
      });
      return;
    }

    if (!result.success) {
      const next = { ...result.errors };
      if (result.summary.length) next.activation = result.summary.join(' ');
      setErrors(next);
      return;
    }

    navigate(LIFECYCLE_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`lifecycle-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? 'No compatible choices available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const hasValidationErrors = Boolean(
    errors.activation
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.concurrent
    || errors.permission
    || errors.checksUnavailable
    || errors.form,
  );

  if (!canView || (isCreate && !canAdd) || (isEdit && !canEdit)) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Lifecycle Control</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          You do not have permission to {isView ? 'view' : isCreate ? 'add' : 'edit'} this Rule.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    const conditionsPreview = formatLifecycleConditionsDisplay(formData);
    const refs = actionDetails?.references || [];
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Lifecycle Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <TextValue label="Module" value={getLifecycleModuleLabel(formData.module)} />
              <TextValue label="Record Type" value={getRecordTypeLabel(formData.module, formData.recordType)} />
              <div className="sm:col-span-2">
                <TextValue label="End Event" value={getEndEventLabel(formData.endEvent)} />
              </div>
              {endEventDetails?.meaning ? (
                <div className="sm:col-span-2">
                  <TextValue label="End Event meaning" value={endEventDetails.meaning} />
                </div>
              ) : null}
              <div className="sm:col-span-2">
                <TextValue label="Archive Action" value={getArchiveActionLabel(formData.archiveAction)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Covered record and material"
                  value={actionDetails?.coveredMaterial || '—'}
                />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Completion requirements"
                  value={actionDetails?.completionRequirements || '—'}
                />
              </div>
              <TextValue label="Archive destination" value={actionDetails?.destination || '—'} />
              <div className="sm:col-span-2">
                <TextValue
                  label="Required references"
                  value={
                    refs.length
                      ? refs
                        .map((ref) => `${ref.id} · ${ref.label}${ref.available === false ? ' (Unavailable)' : ''}`)
                        .join('\n')
                      : '—'
                  }
                />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : 'No Conditions'}
                />
              </div>
              <TextValue label="Date Added" value={formatLifecycleDateTime(formData.dateAdded || formData.createdOn)} />
              <TextValue label="Added By" value={formData.addedBy || formData.createdBy} />
              <TextValue
                label="Last Updated"
                value={formatLifecycleDateTime(formData.lastUpdated || formData.modifiedOn)}
              />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy || formData.modifiedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Lifecycle Rule' : 'Edit Lifecycle Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="lifecycle-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
        </div>
        {renderSelect('module', 'Module', LIFECYCLE_MODULES, 'Select module')}
        {renderSelect(
          'recordType',
          'Record Type',
          recordTypeOptions,
          formData.module ? 'Select record type' : 'Select module first',
          !formData.module,
        )}
        {renderSelect(
          'endEvent',
          'End Event',
          endEventOptions,
          formData.recordType ? 'Select end event' : 'Select record type first',
          !formData.recordType,
        )}
        {renderSelect(
          'archiveAction',
          'Archive Action',
          archiveActionOptions,
          formData.endEvent ? 'Select archive action' : 'Select end event first',
          !formData.endEvent,
        )}
        <AdminConfigLockedField
          label="Covered record and material"
          value={actionDetails?.coveredMaterial || '—'}
          isView={false}
        />
        <AdminConfigLockedField
          label="Completion requirements"
          value={actionDetails?.completionRequirements || '—'}
          isView={false}
        />
        <AdminConfigLockedField
          label="Archive destination"
          value={actionDetails?.destination || '—'}
          isView={false}
        />
        <AdminConfigLockedField
          label="Required references"
          value={
            (actionDetails?.references || []).length
              ? actionDetails.references
                .map((ref) => `${ref.id} · ${ref.label}`)
                .join('; ')
              : '—'
          }
          isView={false}
        />
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>Optional Conditions</AdminDetailFieldLabel>
          <AdminDetailFieldValue className="py-2">
            {conditionFields.length ? 'No Conditions added' : 'No compatible Conditions available'}
          </AdminDetailFieldValue>
          {conditionFields.length ? (
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1.5 text-sm font-medium text-mediumgray cursor-not-allowed"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
          ) : null}
        </div>
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Change Reason <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <textarea
            id="lifecycle-change-reason"
            placeholder="Enter a change reason"
            value={formData.changeReason || ''}
            onChange={(e) => handleChange('changeReason', e.target.value)}
            className={adminFormDetailTextareaClasses(false, Boolean(errors.changeReason))}
            rows={3}
          />
          {errors.changeReason ? <AdminFormFieldError>{errors.changeReason}</AdminFormFieldError> : null}
        </div>
        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
          </div>
        ) : null}
        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default LifecycleRuleDetail;
